import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "./ProductCard";
import { SectionHead, EASE } from "./Motion";

const STEPS = [
  {
    key: "recipient", title: "Who are you gifting?",
    options: [["for-him", "Him"], ["for-her", "Her"], ["for-parents", "Parents"], ["for-friends", "Friends"], ["for-colleagues", "Colleagues"], ["for-kids", "Kids"]],
  },
  {
    key: "occasion", title: "What's the occasion?",
    options: [["birthday", "Birthday"], ["anniversary", "Anniversary"], ["wedding", "Wedding"], ["housewarming", "Housewarming"], ["congratulations", "Congratulations"], ["just-because", "Just Because"]],
  },
  {
    key: "budget", title: "What's your budget?",
    options: [["under-500", "Under ₹500"], ["500-1000", "₹500 – ₹1,000"], ["1000-2500", "₹1,000 – ₹2,500"], ["2500-5000", "₹2,500 – ₹5,000"], ["5000-plus", "₹5,000+"]],
  },
];

export default function GiftFinder({ onNotify, title }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const choose = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    api.post("/events", { type: "gift_finder", meta: { step: key, value } }).catch(() => {});
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const params = new URLSearchParams({ recipient: next.recipient, occasion: next.occasion, budget: next.budget });
        let { data } = await api.get(`/products?${params}`);
        if (data.products.length === 0) {
          ({ data } = await api.get(`/products?budget=${next.budget}`));
        }
        setResults(data.products.slice(0, 4));
      } catch {
        setResults([]);
      }
      setLoading(false);
      setStep(3);
    }
  };

  const reset = () => { setStep(0); setAnswers({}); setResults(null); };

  return (
    <div className="card-surface rounded-3xl p-6 sm:p-12 relative overflow-hidden" data-testid="gift-finder-container">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#7E1E3F]/10 blur-3xl pointer-events-none" />
      <SectionHead overline="The Gift Finder" title={title || "Find Their Perfect Gift"}
        sub="Three questions. One unforgettable gift." testid="gift-finder-head" />

      {step < 3 && (
        <div className="flex gap-2 mb-8" data-testid="gift-finder-progress">
          {STEPS.map((s, i) => (
            <div key={s.key} className="h-1 flex-1 rounded-full overflow-hidden bg-[#F3F4F6]">
              <motion.div className="h-full bg-[#7E1E3F]" initial={false}
                animate={{ width: i < step ? "100%" : i === step ? "50%" : "0%" }}
                transition={{ duration: 0.5, ease: EASE }} />
            </div>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step < 3 ? (
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.4, ease: EASE }} data-testid={`wizard-step-${step + 1}`}>
            <h3 className="font-serif-lux text-2xl text-[#2E1A22] mb-6">{STEPS[step].title}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {STEPS[step].options.map(([value, label]) => (
                <button key={value} onClick={() => choose(STEPS[step].key, value)}
                  className="border border-[#7E1E3F]/25 rounded-xl px-4 py-5 text-sm text-[#45303A] hover:border-[#7E1E3F] hover:bg-[#7E1E3F]/10 hover:-translate-y-1 transition-[border-color,background-color,transform] duration-300"
                  data-testid={`wizard-option-${value}`}>
                  {label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-6 flex items-center gap-2 text-xs text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="wizard-back">
                <ArrowLeft size={14} /> Back
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} data-testid="wizard-result">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-[#7E1E3F]" size={20} />
              <h3 className="font-serif-lux text-2xl text-[#2E1A22]">We Found Something Special</h3>
            </div>
            {loading ? (
              <p className="text-[#857078] text-sm">Curating your matches…</p>
            ) : results && results.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {results.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={onNotify} />)}
              </div>
            ) : (
              <p className="text-[#857078] text-sm">No perfect match yet — our full collection launches soon. Try different answers.</p>
            )}
            <button onClick={reset} className="mt-8 flex items-center gap-2 text-xs tracking-wider uppercase text-[#7E1E3F] hover:text-[#5C1230] transition-colors" data-testid="wizard-restart">
              Start Over <ArrowRight size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
